import { Component, Spx, Style } from '@wix/thunderbolt-becky-types'
import { getStyleCssVars } from '@wix/thunderbolt-becky-root'
import { createCssCompNode } from '../cssCompNode'
import { FeatureRefs } from '../cssFeatures.types'
import { mapValuesValues } from '../../utils/mapValuesValues'
import { mapValues } from '../../utils/mapValues'
import { envRefs, themeRefs } from '@wix/thunderbolt-catharsis'
import { clearFutileOverridesFromCssObject } from '../../utils/futileCssOptimizer'

const COMP_TYPES_WITH_SPECIFIC_VARS = new Set([
	'TPASection',
	'TPAMultiSection',
	'TPAWidget',
	'TPAGluedWidget',
	'tpaWidgetNative',
	'SlideShowSlide',
])

const styleToCssVars = (
	style: Style,
	siteColors: Array<string>,
	isScalePropExperimentOn: boolean,
	themeSpx: Spx | undefined,
	componentsSkinParams: any
) => {
	if (!style?.style?.properties || 'st-css' in style.style.properties) {
		return undefined
	}
	return getStyleCssVars(style, isScalePropExperimentOn, themeSpx, siteColors, componentsSkinParams)
}

const compThemeCompNode = createCssCompNode('styleBased', 'compTheme', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) =>
		COMP_TYPES_WITH_SPECIFIC_VARS.has(component.componentType)
			? null
			: {
					compStyles: refs.compStyles(component),
					sitesEditorName: envRefs.editorName,
					theme: themeRefs.theme,
					experiments: envRefs.experiments,
					siteColors: themeRefs.siteColors,
					componentsSkinParams: envRefs.componentsSkinParams,
			  },
	toViewItem: (_, deps) => {
		if (!deps || !deps.compStyles) {
			return null
		}

		const { compStyles, sitesEditorName, theme, experiments, siteColors } = deps
		const isSiteFromEditor = sitesEditorName === 'Studio' || !!experiments['specs.thunderbolt.forceStudio']
		if (compStyles.type === 'classic') {
			return {
				type: 'classic' as const,
				css: mapValues(compStyles.css, (css: Style) =>
					styleToCssVars(css, siteColors, isSiteFromEditor, theme?.spx, deps.componentsSkinParams)
				),
			}
		}

		const variantsCss = mapValuesValues(compStyles.css, (css: Style) =>
			styleToCssVars(css, siteColors, isSiteFromEditor, theme?.spx, deps.componentsSkinParams)
		)
		return {
			type: 'variants' as const,
			css: variantsCss,
		}
	},
})

const viewerCompThemeCompNode = createCssCompNode('styleBased', 'compTheme', {
	getDependencies: compThemeCompNode.getDependencies,
	toViewItem: (component, deps) => {
		const compTheme = compThemeCompNode.toViewItem(component, deps)
		if (!compTheme || compTheme.type === 'classic') {
			return compTheme
		}
		return {
			...compTheme,
			css: clearFutileOverridesFromCssObject(compTheme.css),
		}
	},
})

export const compTheme = (isEditor: boolean) => (isEditor ? compThemeCompNode : viewerCompThemeCompNode)
